// React Imports
import React from "react";
import PropTypes from "prop-types";

// React Spectrum Components
import { Grid, GridRow, GridColumn } from "@react/react-spectrum/Grid";
import AdobeLogo from "@react/react-spectrum/Icon/AdobeLogo";

// Styles
import "../css/Header.scss";

/**
 * Component for displaying the application header.
 *
 * This component displays the application title and the Adobe IMS component.
 * It requires React Spectrum components and allows Adobe IMS, and children
 * components for the text. You can also specify if you want the Adobe logo included
 * and specify a function or hyperlink when the header text is clicked.
 *
 * @component
 * @example
 *
 * const sampleFunction = () => {
 *    alert("hello!");
 * }
 *
 * return (
 *   <Header
 *      includeAdobeLogo
 *      imsComponent={<AdobeIMS susiLocale="en_US" />}
 *      onTextClick={sampleFunction}
 *      linkHref="https://www.google.com/"
 *      linkTarget="_blank"
 *    >
 *      Adobe Partner Connection Registration App
 *    </Header>
 * )
 */
const Header = ({
  includeAdobeLogo,
  children,
  imsComponent,
  onTextClick,
  linkHref,
  linkTarget,
  className,
  hideImsComponent,
}) => {
  // Renders the Adobe Logo icon if requested.
  const renderAdobeLogo = () => {
    if (includeAdobeLogo) {
      return <AdobeLogo className="adobe-icon" />;
    }
    return "";
  };

  // Renders the header text based on if it is a link, function, or pure text.
  const renderText = () => {
    // Custom function
    if (typeof onTextClick === "function") {
      return (
        <div
          role="button"
          tabIndex={0}
          className="app-title clickable"
          onClick={onTextClick}
          onKeyPress={onTextClick}
        >
          {renderAdobeLogo()}
          <span className="app-name">{children}</span>
        </div>
      );
    }
    // Hyperlink
    if (linkHref) {
      let finalLinkTarget = "_self";
      switch (linkTarget) {
        case "_blank":
        case "blank":
          finalLinkTarget = "_blank";
          break;
        default:
          break;
      }
      return (
        <a
          href={linkHref}
          target={finalLinkTarget}
          rel="noopener noreferrer"
          className="app-title"
        >
          {renderAdobeLogo()}
          <span className="app-name">{children}</span>
        </a>
      );
    }
    // Plain text
    return (
      <div className="app-title">
        {renderAdobeLogo()}
        <span className="app-name">{children}</span>
      </div>
    );
  };

  return (
    <div className={`cmp-header ${className}`}>
      <Grid variant="fixed" className="header-grid">
        <GridRow>
          <GridColumn size={[9, 7, 9, 9]} className="col-app-title">
            {renderText()}
          </GridColumn>
          <GridColumn
            size={[3, 5, 3, 3]}
            className="ims-right"
            style={hideImsComponent ? { display: "none" } : { display: "flex" }}
          >
            {imsComponent}
          </GridColumn>
        </GridRow>
      </Grid>
    </div>
  );
};
Header.propTypes = {
  /**
   * includeAdobeLogo {Boolean} Whether to render the Adobe Logo in the top left.
   */
  includeAdobeLogo: PropTypes.bool.isRequired,
  /**
   * children {React Component(s)} Elements for rendering in the header component. The
   * choice was made to accept elements instead of a plain string to allow for i18n
   * compatibility.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  /**
   * imsComponent {React Component} The IMS component to render on the right of the
   * header component.
   */
  imsComponent: PropTypes.element,
  /**
   * onTextClick {Function} Function to bind to the on-click event of the header
   * text. If the href property is provided, this will take prescendence.
   */
  onTextClick: PropTypes.func,
  /**
   * linkHref {String} If provided, will turn the header text into a hyperlink with this
   * value as the href. If onTextClick prop is provided, this prop will have no effect.
   */
  linkHref: PropTypes.string,
  /**
   * linkTarget {String} Used in conjunction with href, to specify that the hyperlink should
   * open in the same or a new window. Defaults to "_self".
   *
   * Accepts "blank" or "_blank".
   */
  linkTarget: PropTypes.string,
};
Header.defaultProps = {
  imsComponent: null,
  onTextClick: null,
  linkHref: null,
  linkTarget: "_self",
};

export default Header;
